import { Injectable, Injector } from "@angular/core";
import * as signalR from "@microsoft/signalr";
import { AfAuctionModel, AfAuctionResult, AfAuctionStatusTypes, AfBidModel, AfBidStatusTypes, AfResultService } from "@pattonair/auction-framework";
import { MfAuthService, MfBaseService, MfDialogService, MfError, MfModelConfigMapped, MfModelConfigService, MfPortalsClientService, MfTypeInfo } from "@pattonair/material-framework";
import { catchError, from, map, Observable, of, Subject } from "rxjs";
import { AUC_ENVIRONMENT } from "../environments/environment";
import { AucModelsConfig } from "../modelConfigs/model.config";

const TYPE_INFO: MfTypeInfo = { className: "AucAuctionService" };

export type AucAuctionAuctionUpdate = {
  id: string;
  currentPrice: number;
  bidCount: number;
  isReservePriceMet: boolean;
  auctionStatus: AfAuctionStatusTypes;
}

export type AucAuctionWinningUpdate = AucAuctionAuctionUpdate & {
  winning: boolean;
  bidStatus: AfBidStatusTypes;
}

export type AucAuctionSubscriptionStatus = {
  id: string,
  status: string,
  message?: string,
  userId?: string,
};

@Injectable({ providedIn: "root" })
export class AucAuctionService extends MfBaseService {
  public auctionUpdated = new Subject<AucAuctionAuctionUpdate>();
  public winningUpdate = new Subject<AucAuctionWinningUpdate>();
  public subscriptionStatus = new Subject<AucAuctionSubscriptionStatus>();
  public error = new Subject<{ message: string }>();
  
  protected _hubConnection: signalR.HubConnection;
  protected _auctionModelConfig: MfModelConfigMapped;

  public constructor(
    protected override _injector: Injector,
    protected _authService: MfAuthService,
    protected _portalsClientService: MfPortalsClientService,
    protected _modelConfigService: MfModelConfigService,
    protected _resultService: AfResultService,
    protected _dialog: MfDialogService,
  ) {
    super(TYPE_INFO, _injector);
    this._auctionModelConfig = this._modelConfigService.get<AucModelsConfig>("auction");

    this._hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${AUC_ENVIRONMENT.auctionManagementRootUrl}/${AUC_ENVIRONMENT.auctionManagementUpdateHubUrl}`, {
        accessTokenFactory: () => {
          return this._authService.getAccessToken();
        }
      })
      .withAutomaticReconnect()
      .build();
    this._registerHandlers();
  }

  public startConnection(): Observable<void> {
    if (this._hubConnection.state === signalR.HubConnectionState.Disconnected) {
      return from(this._hubConnection.start());
    }
    return of(undefined);
  }

  public stopConnection(): Observable<void> {
    if (this._hubConnection.state !== signalR.HubConnectionState.Disconnected) {
      return from(this._hubConnection.stop());
    }
    return of();
  }

  public subscribeToAuction(auctionId: string): Observable<void> {
    return from(this._hubConnection.invoke("SubscribeToAuction", auctionId));
  }

  public unsubscribeFromAuction(auctionId: string): Observable<void> {
    return from(this._hubConnection.invoke("UnsubscribeFromAuction", auctionId));
  }

  public getBiddingLive(): Observable<AfAuctionModel[]> {
    return this._portalsClientService.getItemGet<AfAuctionModel[]>(
      this._auctionModelConfig,
      `${AUC_ENVIRONMENT.auctionManagementRootUrl}/${AUC_ENVIRONMENT.auctionManagementAuctionsUrl}/bidding`,
    ).pipe(
      map((result) => this._resultService.getResult(result)),
    );
  }

  public getWonLive(): Observable<AfAuctionModel[]> {
    return this._portalsClientService.getItemGet<AfAuctionModel[]>(
      this._auctionModelConfig,
      `${AUC_ENVIRONMENT.auctionManagementRootUrl}/${AUC_ENVIRONMENT.auctionManagementAuctionsUrl}/won`,
    ).pipe(
      map((result) => this._resultService.getResult(result)),
    );
  }

  public getAllLive(): Observable<AfAuctionModel[]> {
    return this._portalsClientService.getItemGet<AfAuctionModel[]>(
      this._auctionModelConfig,
      `${AUC_ENVIRONMENT.auctionManagementRootUrl}/${AUC_ENVIRONMENT.auctionManagementAuctionsUrl}/live`,
    ).pipe(
      map((result) => this._resultService.getResult(result)),
    );
  }

  public getLive(auctionId: string): Observable<AfAuctionModel> {
    return this._portalsClientService.getItemGet<AfAuctionModel>(
      this._auctionModelConfig,
      `${AUC_ENVIRONMENT.auctionManagementRootUrl}/${AUC_ENVIRONMENT.auctionManagementAuctionsUrl}/live/${auctionId}`,
    ).pipe(
      map((result) => this._resultService.getResult(result)),
    );
  }

  public bid(bid: AfBidModel): Observable<boolean> {
    return this._portalsClientService.getItemPost<AfAuctionResult<boolean> | boolean, AfAuctionModel>(
      this._auctionModelConfig,
      this._auctionModelConfig,
      bid,
      `${AUC_ENVIRONMENT.auctionManagementRootUrl}/${AUC_ENVIRONMENT.auctionManagementBidsUrl}`,
    ).pipe(
      catchError((error: MfError) => {
        const errors = this._resultService.getErrors(error);
        const dialogRef = this._dialog.openOkCancel({
          disableClose: true,
          data: {
            cancelShow: true,
            cancelDisplayName: "Close",
            okShow: false,
            messageInnerHTML: errors.join("<br/>"),
            title: "Bid Failed",
          }
        });

        return dialogRef.afterClosed();
      }),
      map((result) => this._resultService.getResult(result)),
    );
  }

  protected _registerHandlers() {
    this._hubConnection.on("ReceiveAuctionUpdate", (update: AucAuctionAuctionUpdate) => {
      this.auctionUpdated.next(update);
    });

    this._hubConnection.on("ReceiveWinningUpdate", (update: AucAuctionWinningUpdate) => {
      this.winningUpdate.next(update);
    });

    this._hubConnection.on("SubscriptionStatus", (status: AucAuctionSubscriptionStatus) => {
      this.subscriptionStatus.next(status);
    });

    this._hubConnection.on("Error", (error: { message: string }) => {
      this.error.next(error);
    });
  }
}
